import { ArticleFormValues, BlogRecoverySnapshot } from 'Models'

/**
 * Pure localStorage read/write for autosave recovery snapshots. No React
 * here by design — useBlogRecovery.ts wraps this for component use.
 *
 * Each article (or each not-yet-created draft) gets its own key, never a
 * single shared key, per the spec ("Do not use a generic shared key such as
 * 'article-editor-content'. That could cause content from one article to
 * appear in another."). An index of all snapshot keys is kept alongside so
 * a brand-new, not-yet-saved draft (no slug yet) can still be found again
 * after a refresh — see KNOWN LIMITATION below.
 */

const PREFIX = 'tg-blog-recovery:'
const INDEX_KEY = 'tg-blog-recovery-index'

interface RecoveryIndexEntry {
  key: string
  articleSlug: string | null
  title: string
  updatedAt: string
}

const snapshotKey = (identifier: string): string => `${PREFIX}${identifier}`

const readIndex = (): RecoveryIndexEntry[] => {
  try {
    const raw = localStorage.getItem(INDEX_KEY)
    return raw ? (JSON.parse(raw) as RecoveryIndexEntry[]) : []
  } catch {
    return []
  }
}

const writeIndex = (entries: RecoveryIndexEntry[]): void => {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(entries))
  } catch {
    // localStorage can throw (quota, private mode) — recovery is best-effort, never fatal.
  }
}

/**
 * Saves (or overwrites) the recovery snapshot for one article/draft
 * identifier. Called on every form change, debounced by the caller.
 */
export const saveRecoverySnapshot = (identifier: string, snapshot: BlogRecoverySnapshot): void => {
  try {
    localStorage.setItem(snapshotKey(identifier), JSON.stringify(snapshot))

    const index = readIndex().filter((entry) => entry.key !== identifier)
    index.unshift({
      key: identifier,
      articleSlug: snapshot.articleSlug,
      title: snapshot.values.title || '(untitled)',
      updatedAt: snapshot.updatedAt,
    })
    writeIndex(index.slice(0, 20)) // cap the index so it can't grow unbounded across many abandoned drafts
  } catch {
    // Same as above — best-effort.
  }
}

export const readRecoverySnapshot = (identifier: string): BlogRecoverySnapshot | null => {
  try {
    const raw = localStorage.getItem(snapshotKey(identifier))
    return raw ? (JSON.parse(raw) as BlogRecoverySnapshot) : null
  } catch {
    return null
  }
}

/** Clears one snapshot — call only after a confirmed successful save, or an explicit user "discard". Never call this just because a save was attempted. */
export const clearRecoverySnapshot = (identifier: string): void => {
  try {
    localStorage.removeItem(snapshotKey(identifier))
    writeIndex(readIndex().filter((entry) => entry.key !== identifier))
  } catch {
    // best-effort
  }
}

/**
 * For the "new article, no slug yet" case: lists recoverable snapshots for
 * drafts that were never saved to the backend (articleSlug: null), newest
 * first, so the editor can offer "Resume '<title>' from <time>?" instead of
 * guessing which abandoned draft to restore.
 *
 * KNOWN LIMITATION: the identifier for a brand-new draft is a random id
 * generated when the editor mounts (see useBlogRecovery.ts). If the admin
 * refreshes before that id is ever written to this index (i.e. before the
 * 2-second debounce's first write), that specific snapshot becomes
 * unreachable by exact key — though it still shows up here via the index
 * scan, since the index entry itself is written synchronously with the
 * first snapshot save, same tick. The gap this doesn't cover is a tab
 * closed before ANY change was made at all — nothing to recover in that
 * case regardless.
 */
export const listUnsavedDraftSnapshots = (): RecoveryIndexEntry[] =>
  readIndex().filter((entry) => entry.articleSlug === null)

export const findSnapshotForSlug = (slug: string): BlogRecoverySnapshot | null =>
  readRecoverySnapshot(slug)

// ---------------------------------------------------------------------------
// Conflict backup: when a 409 happens, the admin's unsaved work is copied to
// its own key. The normal snapshot keeps being overwritten as they continue
// editing; this copy is only removed on an explicit discard/restore.
// ---------------------------------------------------------------------------

const conflictKey = (slug: string): string => `${PREFIX}${slug}:conflict`

export const saveConflictBackup = (slug: string, snapshot: BlogRecoverySnapshot): void => {
  try {
    localStorage.setItem(conflictKey(slug), JSON.stringify(snapshot))
  } catch {
    // best-effort
  }
}

export const readConflictBackup = (slug: string): BlogRecoverySnapshot | null => {
  try {
    const raw = localStorage.getItem(conflictKey(slug))
    return raw ? (JSON.parse(raw) as BlogRecoverySnapshot) : null
  } catch {
    return null
  }
}

export const clearConflictBackup = (slug: string): void => {
  try {
    localStorage.removeItem(conflictKey(slug))
  } catch {
    // best-effort
  }
}

export const buildSnapshot = (
  articleSlug: string | null,
  baseVersion: number | null,
  changeCounter: number,
  values: ArticleFormValues,
): BlogRecoverySnapshot => ({
  articleSlug,
  baseVersion,
  updatedAt: new Date().toISOString(),
  changeCounter,
  values,
})
